import React from 'react';
import { Badge } from '@/components/ui/badge';

const SECTION_GRADIENTS = ['from-sky-600 to-cyan-500', 'from-teal-600 to-emerald-500', 'from-indigo-600 to-violet-500', 'from-blue-700 to-sky-500', 'from-fuchsia-600 to-pink-500', 'from-emerald-600 to-lime-500', 'from-violet-600 to-purple-500', 'from-cyan-600 to-teal-500'];

export const PlannerSections = ({ doc, testId }) => {
  if (!doc) return <p className="text-sm text-slate-500 italic py-8 text-center">This sheet is not in the uploaded workbook.</p>;
  return (
    <div className="space-y-6" data-testid={testId}>
      <div className="rounded-2xl bg-gradient-to-br from-slate-800 to-slate-700 text-white p-5">
        <h2 className="text-lg font-semibold">{doc.title}</h2>
        {doc.subtitle && <p className="text-sm text-slate-200 mt-1">{doc.subtitle}</p>}
      </div>
      {doc.sections.map((sec, i) => (
        <div key={i} className="rounded-2xl border bg-white shadow-sm overflow-hidden" data-testid={`${testId}-section-${i}`}>
          {sec.title && <div className={`bg-gradient-to-r ${SECTION_GRADIENTS[i % SECTION_GRADIENTS.length]} text-white px-5 py-2.5 text-sm font-semibold`}>{sec.title}</div>}
          {sec.type === 'table' ? <SectionTable headers={sec.headers} rows={sec.rows} /> : (
            <div className="p-5 space-y-2">{sec.lines.map((l, j) => <p key={j} className="text-sm text-slate-700 whitespace-pre-line">{l}</p>)}</div>
          )}
        </div>
      ))}
    </div>
  );
};

const isLevel = (v) => /^L[1-4]\b/.test(String(v || ''));
const LEVEL_CLASS = { L1: 'bg-sky-100 text-sky-800', L2: 'bg-teal-100 text-teal-800', L3: 'bg-indigo-100 text-indigo-800', L4: 'bg-violet-100 text-violet-800' };

function SectionTable({ headers, rows }) {
  const cols = Math.max(headers.length, ...rows.map((r) => r.length));
  const numeric = (v) => /^\d+(\.\d+)?$/.test(String(v || '').trim());
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>{Array.from({ length: cols }).map((_, j) => <th key={j} className={`px-3 py-2 text-left ${isLevel(headers[j]) ? 'text-center' : ''}`}>{headers[j] || ''}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t align-top hover:bg-slate-50/70">
              {Array.from({ length: cols }).map((_, j) => {
                const v = r[j] || '';
                const first = j === 0 || (j === 1 && !r[0]);
                if (isLevel(headers[j]) && numeric(v)) {
                  const lvl = String(headers[j]).slice(0, 2);
                  return <td key={j} className="px-3 py-2 text-center"><span className={`inline-block min-w-8 rounded-full px-2 py-0.5 text-xs font-semibold ${LEVEL_CLASS[lvl] || 'bg-slate-100'}`}>{v}</span></td>;
                }
                if (isLevel(v) && v.length <= 3) return <td key={j} className="px-3 py-2"><Badge className={LEVEL_CLASS[v.slice(0, 2)] || ''} variant="outline">{v}</Badge></td>;
                return <td key={j} className={`px-3 py-2 whitespace-pre-line ${first ? 'font-medium text-slate-900' : 'text-slate-700'} ${numeric(v) ? 'text-center font-semibold' : ''}`}>{v}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
